import React, { useState, useEffect, useRef } from 'react';
import {
  ShoppingBag, Utensils, Landmark, ShoppingCart, Truck,
  ChevronRight, Camera, CheckCircle2, Fingerprint, Database,
  Zap, AlertTriangle, Image as ImageIcon, Building2,
  Tags, Scale, BrainCircuit, UploadCloud,
} from 'lucide-react';

import AssetPericialService from '../../services/AssetPericialService';
import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, FATORES_CONFIG, proximaEtapa } from '../../constants/vistoria';

const FATOR_ID = ETAPA.F05_COMERCIO;
const FATOR_CONFIG = FATORES_CONFIG[FATOR_ID];

const DICIONARIO_ABNT = {
  densidadeComercial: [
    'POLO COMERCIAL CONSOLIDADO (SHOPPINGS/CALÇADÕES)',
    'COMÉRCIO LOCAL INTENSO (AVENIDAS PRINCIPAIS)',
    'COMÉRCIO DE VIZINHANÇA (PADARIAS/MERCADOS)',
    'SERVIÇOS PREDOMINANTES (ESCRITÓRIOS/CLÍNICAS)',
    'COMÉRCIO EM TRANSIÇÃO (BAIRRO EM CRESCIMENTO)',
    'ZONA DE COMÉRCIO ATACADISTA E LOGÍSTICA',
    'AUSÊNCIA DE COMÉRCIO (ZONA RESIDENCIAL DORMITÓRIO)',
  ],
  setoresPresentes: [
    'ALIMENTAÇÃO E ENTRETENIMENTO (GASTRONOMIA/LAZER)',
    'VESTUÁRIO E ACESSÓRIOS (VÍTRINES E MODA)',
    'MATERIAIS DE CONSTRUÇÃO E DECORAÇÃO',
    'AUTOMOTIVO, PEÇAS E SERVIÇOS TÉCNICOS',
    'SETOR DE SAÚDE (FARMÁCIAS E CONSULTÓRIOS)',
    'SERVIÇOS FINANCEIROS E ADMINISTRATIVOS',
    'MISTO / DIVERSIFICADO (PRESENÇA DE TODOS OS SETORES)',
    'SETOR DE CONVENIÊNCIA APENAS',
  ],
  bancosServicos: [
    'CONCENTRAÇÃO DE AGÊNCIAS BANCÁRIAS (HUB FINANCEIRO)',
    'AGÊNCIAS BANCÁRIAS ESPORÁDICAS (PRINCIPAIS BANDEIRAS)',
    'LOTÉRICAS E CORRESPONDENTES BANCÁRIOS APENAS',
    'CAIXAS ELETRÔNICOS (ATM MULTIBANCO) APENAS',
    'INSTITUIÇÕES DE ENSINO / SAÚDE PREDOMINANTES',
    'INEXISTENTE NA ZONA DE INFLUÊNCIA',
  ],
  estacionamentoRegiao: [
    'ESTACIONAMENTOS PRIVADOS ABUNDANTES E ACESSÍVEIS',
    'ZONA AZUL / VAGAS ROTATIVAS PÚBLICAS DISPONÍVEIS',
    'ESTACIONAMENTO APENAS EM VIAS PÚBLICAS (LIMITADO)',
    'DIFICULDADE ACENTUADA DE ESTACIONAMENTO (SATURADO)',
    'VIA COM PROIBIÇÃO DE ESTACIONAMENTO (EIXO RÁPIDO)',
    'VALET SERVICE PREDOMINANTE NAS ATIVIDADES',
    'ESTACIONAMENTO RESTRITO A CARGA E DESCARGA',
  ],
  logisticaCarga: [
    'ZONAS DE CARGA E DESCARGA DEMARCADAS E EXCLUSIVAS',
    'PERMITE TRÁFEGO DE CAMINHÕES PESADOS (EIXO LOGÍSTICO)',
    'RESTRITO A VEÍCULOS LEVES E VUCs (URBANO)',
    'ACESSO LOGÍSTICO COMPROMETIDO (VIA ESTREITA)',
    'HORÁRIOS RÍGIDOS DE CARGA/DESCARGA (LEGISLAÇÃO)',
    'ACESSO APENAS PARA MORADORES E PASSEIO',
  ],
};

const PADRAO_SISTEMA = {
  densidadeComercial: 'COMÉRCIO LOCAL INTENSO (AVENIDAS PRINCIPAIS)',
  setoresPresentes: 'MISTO / DIVERSIFICADO (PRESENÇA DE TODOS OS SETORES)',
  bancosServicos: 'LOTÉRICAS E CORRESPONDENTES BANCÁRIOS APENAS',
  estacionamentoRegiao: 'ESTACIONAMENTO APENAS EM VIAS PÚBLICAS (LIMITADO)',
  logisticaCarga: 'RESTRITO A VEÍCULOS LEVES E VUCs (URBANO)',
};

const ITENS_UI = [
  { id: 'densidadeComercial',   label: 'Densidade de Varejo',   icon: ShoppingBag,  desc: 'Volume de estabelecimentos comerciais na zona' },
  { id: 'setoresPresentes',     label: 'Mix de Atividades',     icon: Utensils,     desc: 'Diversidade de ramos e serviços identificados' },
  { id: 'bancosServicos',       label: 'Bancos e Serviços',     icon: Landmark,     desc: 'Presença de rede bancária e serviços públicos' },
  { id: 'estacionamentoRegiao', label: 'Estacionamento',        icon: ShoppingCart, desc: 'Condições de parada e acessibilidade perimetral' },
  { id: 'logisticaCarga',       label: 'Capacidade Logística',  icon: Truck,        desc: 'Infraestrutura de suporte para carga e descarga' },
];

export default function VistoriaRegiaoFator05Comercio({ setNavegacao, infoLaudo, peritoAtivo }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [activePhotoSlot, setActivePhotoSlot] = useState(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [loadingCaptura, setLoadingCaptura] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const [dadosComercio, setDadosComercio] = useState({
    ...PADRAO_SISTEMA,
    fatorValorizacao: '1.00',
    textoConsolidado: '',
    justificativaDivergencia: '',
    evidencias: {},
    justificativas: {},
  });

  const divergencias = Object.keys(PADRAO_SISTEMA).filter(
    k => dadosComercio[k] !== PADRAO_SISTEMA[k]
  );

  const statusAuditoria = AuditoriaNormativaService.validarFinalizacao({
    ...dadosComercio,
    justificativaResumo: dadosComercio.justificativaDivergencia,
    _divergencias: divergencias,
  });

  useEffect(() => {
    const desvio = AuditoriaNormativaService.calcularDesvio(dadosComercio.fatorValorizacao);
    const sinal = desvio >= 0 ? '+' : '';

    const base =
      `Para a determinação do valor de mercado, aplicou-se o **Fator de Localização Regional — ` +
      `Comércio e Serviços**, justificando-se a valoração de **${sinal}${desvio.toFixed(2)}%** em ` +
      `virtude da infraestrutura comercial disponível na zona de influência do imóvel. A densidade ` +
      `de varejo é classificada como ${dadosComercio.densidadeComercial}, com mix de atividades ` +
      `${dadosComercio.setoresPresentes}. O cálculo seguiu a fórmula: $V_h = V_b \\times (1 + i)$.`;

    const adendo = dadosComercio.justificativaDivergencia
      ? `\n\n**Justificativa de Comércio:** ${dadosComercio.justificativaDivergencia}`
      : '';

    const fechamento =
      `\n\nFundamentação: **NBR 14653-2:2011, Item ${FATOR_CONFIG.nbrItem}**.`;

    setDadosComercio(prev => ({
      ...prev,
      textoConsolidado: base + adendo + fechamento,
    }));
  }, [
    dadosComercio.fatorValorizacao,
    dadosComercio.justificativaDivergencia,
    dadosComercio.densidadeComercial,
    dadosComercio.setoresPresentes,
  ]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activePhotoSlot) return;
    setErro(null);
    setLoadingCaptura(true);

    try {
      const resultado = await AssetPericialService.processarCaptura(
        file,
        FATOR_ID,
        infoLaudo?.numero || 'PGLP-F5',
        { perito: peritoAtivo, comodoLocal: activePhotoSlot, tentarGPS: true }
      );

      setDadosComercio(prev => ({
        ...prev,
        evidencias: {
          ...prev.evidencias,
          [activePhotoSlot]: {
            url: resultado.url,
            file: resultado.url,
            hashIntegridade: resultado.hashIntegridade,
            fileName: resultado.fileName,
            metadados: resultado.metadados,
          },
        },
      }));
      setShowPhotoOptions(false);
    } catch (err) {
      setErro(err.message || 'Falha ao processar captura');
    } finally {
      setLoadingCaptura(false);
      e.target.value = null;
    }
  };

  const salvarEAvancar = async () => {
    if (statusAuditoria.isBloqueado) {
      setErro(`BLOQUEIO PERICIAL: ${statusAuditoria.mensagem}`);
      return;
    }
    setIsSaving(true);
    setErro(null);

    try {
      await laudoApi.salvarModulo({
        report_id: infoLaudo?.numero || 'REPORT-F5',
        perito: peritoAtivo || 'MONTE_HERMOM_USER',
        fator: FATOR_ID,
        codigo: FATOR_CONFIG.codigo,
        pasta: FATOR_CONFIG.pasta,
        data_execucao: new Date().toISOString(),
        statusRevisao: 'PENDENTE',
        ...dadosComercio,
      });
      setNavegacao(proximaEtapa(FATOR_ID));
    } catch (err) {
      if (err._enfileirado) {
        setErro('Sem conexão — dados salvos localmente.');
        setTimeout(() => setNavegacao(proximaEtapa(FATOR_ID)), 1200);
      } else {
        setErro(`Falha ao gravar: ${err.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isDivergente = (campo) => dadosComercio[campo] !== PADRAO_SISTEMA[campo];

  return (
    <div className="max-w-5xl mx-auto space-y-12 italic font-black relative pb-32 uppercase">
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[120px] z-0 select-none text-[#2F353B]">
        MONTE HERMOM
      </div>

      <input type="file" ref={cameraInputRef} onChange={onFileChange} accept="image/*" capture="environment" className="hidden" />
      <input type="file" ref={fileInputRef} onChange={onFileChange} accept="image/*" className="hidden" />

      <header className="flex justify-between items-end border-b-8 border-[#2F353B] pb-10 relative z-10">
        <div className="space-y-5">
          <div className="flex items-center gap-4 text-blue-600">
            <BrainCircuit size={28} className="animate-pulse" />
            <span className="text-[12px] tracking-[0.7em] text-slate-400 font-black uppercase">
              Auditoria de Comércio e Serviços
            </span>
          </div>
          <h2 className="text-[26px] text-[#2F353B] tracking-[0.3em] leading-none uppercase">
            {FATOR_CONFIG.titulo.split('. ')[1]} <span className="text-blue-600">E SERVIÇOS</span>
          </h2>
        </div>
        <div className="bg-[#2F353B] px-12 py-6 rounded-[2.5rem] border-b-4 border-blue-600 shadow-2xl text-white text-[16px] font-black italic">
          REPORT Nº {infoLaudo?.numero || '2026-F5'}
        </div>
      </header>

      {erro && (
        <div className="bg-red-900/90 text-white p-6 rounded-[2rem] flex items-center gap-6 relative z-10">
          <AlertTriangle size={24} />
          <span className="text-[11px] font-black italic tracking-widest">{erro}</span>
        </div>
      )}

      <section className="grid grid-cols-1 gap-10 relative z-10">
        {ITENS_UI.map((item) => {
          const divergente = isDivergente(item.id);
          const temFoto = !!dadosComercio.evidencias[item.id];

          return (
            <div
              key={item.id}
              className={`p-2 rounded-[6rem] border-4 transition-all duration-500 shadow-sm ${
                divergente
                  ? 'bg-blue-50 border-blue-600 shadow-blue-100'
                  : 'bg-white border-slate-100 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-10 p-6">
                <div className="flex items-center gap-10 w-[40%] border-r-4 border-slate-50 mr-10 py-3">
                  <div className={`p-7 rounded-[3.5rem] shadow-2xl transition-all ${divergente ? 'bg-blue-600 text-white scale-110' : 'bg-slate-50 text-blue-600'}`}>
                    <item.icon size={28} strokeWidth={2.5} />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[14px] tracking-widest font-black italic uppercase leading-tight mb-2">
                      {item.label}
                    </label>
                    <span className="text-[9px] text-slate-400 font-black italic uppercase tracking-widest">
                      {item.desc}
                    </span>
                  </div>
                </div>

                <div className="flex-1 relative group">
                  <select
                    value={dadosComercio[item.id]}
                    onChange={(e) => setDadosComercio(prev => ({ ...prev, [item.id]: e.target.value }))}
                    className="w-full bg-slate-50 border-none rounded-[3rem] p-8 text-slate-800 text-[14px] outline-none shadow-inner font-black italic appearance-none cursor-pointer hover:bg-slate-100 transition-all uppercase"
                  >
                    {DICIONARIO_ABNT[item.id].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none text-blue-600">
                    <Tags size={22} />
                  </div>
                </div>

                <button
                  onClick={() => { setActivePhotoSlot(item.id); setShowPhotoOptions(true); }}
                  className={`ml-10 p-7 rounded-full border-4 transition-all ${
                    temFoto
                      ? 'bg-blue-600 border-blue-600 text-white shadow-xl'
                      : 'bg-white border-slate-100 text-slate-200 hover:text-blue-600 hover:border-blue-600'
                  }`}
                >
                  {temFoto ? <CheckCircle2 size={28} /> : <Camera size={28} />}
                </button>
              </div>

              {divergente && !temFoto && (
                <div className="mt-4 ml-32 mb-6">
                  <textarea
                    value={dadosComercio.justificativas[item.id] || ''}
                    onChange={(e) => setDadosComercio(prev => ({
                      ...prev,
                      justificativas: { ...prev.justificativas, [item.id]: e.target.value },
                    }))}
                    placeholder="Fundamentação técnica para ausência de evidência..."
                    className="w-full bg-white border-2 border-amber-200 rounded-[2.5rem] p-6 text-[11px] italic font-black text-slate-600 outline-none focus:border-amber-500"
                    rows={2}
                  />
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="bg-[#2F353B] p-20 rounded-[7rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border-b-[20px] border-blue-600 relative overflow-hidden mt-10 z-10">
        <div className="relative z-10 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="bg-[#1e293b] p-14 rounded-[5rem] border-2 border-white/5 text-center shadow-inner">
              <label className="text-[12px] text-blue-400 block mb-8 italic font-black uppercase tracking-[0.5em]">
                Fator de Comércio (i)
              </label>
              <div className="flex items-center justify-center gap-4">
                <Scale className="text-slate-500" size={24} />
                <input
                  type="text"
                  value={dadosComercio.fatorValorizacao}
                  onChange={(e) => setDadosComercio(prev => ({ ...prev, fatorValorizacao: e.target.value }))}
                  className="w-full bg-slate-900/50 py-10 rounded-[4rem] text-[36px] text-white italic outline-none text-center tracking-[0.4em] font-black border-4 border-transparent focus:border-blue-600 transition-all shadow-2xl"
                />
              </div>
            </div>

            <div className="bg-blue-600 p-14 rounded-[5rem] text-center shadow-2xl flex flex-col justify-center items-center">
              <p className="text-white/80 text-[13px] mb-8 font-black italic tracking-[0.2em] uppercase">
                ABNT 14653: $$V_h = V_b \times (1 + i)$$
              </p>
              <h4 className="text-[22px] tracking-[0.4em] text-white italic font-black uppercase drop-shadow-2xl">
                Valor Homogeneizado
              </h4>
            </div>
          </div>

          <div className="bg-white/5 p-14 rounded-[6rem] border-2 border-white/5 space-y-10 backdrop-blur-3xl">
            <div className="flex items-center gap-6 border-b border-white/10 pb-10">
              <Fingerprint className="text-blue-600" size={28} strokeWidth={3} />
              <h3 className="text-[18px] text-white tracking-[0.4em] uppercase font-black italic">
                Redação Pericial Auto-Gen
              </h3>
            </div>
            <textarea
              readOnly
              value={dadosComercio.textoConsolidado}
              className="w-full h-64 bg-transparent border-none text-slate-300 text-[14px] leading-[1.8] resize-none font-black italic outline-none text-justify"
            />
          </div>

          {divergencias.length > 0 && (
            <div className="pt-12 border-t-2 border-white/10 space-y-8">
              <div className="flex items-center gap-5">
                <AlertTriangle size={22} className="text-amber-500 animate-pulse" />
                <label className="text-[12px] text-amber-400 font-black italic uppercase tracking-[0.3em]">
                  Justificativa Técnica de Desvio
                </label>
              </div>
              <textarea
                value={dadosComercio.justificativaDivergencia}
                onChange={(e) => setDadosComercio(prev => ({ ...prev, justificativaDivergencia: e.target.value }))}
                placeholder="DESCREVA AS PARTICULARIDADES LOCAIS QUE JUSTIFICAM O AJUSTE..."
                className="w-full h-44 bg-white/5 rounded-[4rem] p-10 text-white text-[13px] font-black italic outline-none border-2 border-amber-900/30 focus:border-amber-600 transition-all"
              />
            </div>
          )}
        </div>
        <Building2 className="absolute -right-40 -bottom-40 text-white/[0.03] w-[600px] h-[600px] -rotate-12 pointer-events-none" />
      </section>

      <footer className="flex justify-between items-center pt-16 border-t-4 border-slate-100 mt-20 relative z-10">
        <div className="flex flex-col font-black italic">
          <span className="text-[14px] text-[#2F353B] tracking-[0.8em] uppercase">MONTE HERMOM</span>
          <span className="text-[10px] text-blue-600 mt-4 font-black italic uppercase tracking-[0.4em]">
            Asset Integrity & Valuation System 2026
          </span>
        </div>
        <button
          onClick={salvarEAvancar}
          disabled={isSaving}
          className="group flex items-center gap-16 bg-[#2F353B] text-white px-28 py-12 rounded-[6rem] shadow-2xl transition-all text-[20px] font-black italic hover:bg-blue-600 disabled:opacity-50"
        >
          {isSaving ? 'PROCESSANDO...' : 'AVANÇAR FATOR 06'}
          <ChevronRight size={34} className="group-hover:translate-x-8 transition-transform" />
        </button>
      </footer>

      {showPhotoOptions && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-[#2F353B]/98 backdrop-blur-2xl p-10">
          <div className="bg-white w-full max-w-2xl rounded-[7rem] p-20 space-y-16 border-[12px] border-blue-600 shadow-2xl">
            <div className="text-center space-y-8">
              <div className="bg-blue-600 w-24 h-24 rounded-[3rem] flex items-center justify-center mx-auto text-white shadow-2xl">
                <Camera size={40} strokeWidth={2.5} />
              </div>
              <h3 className="text-[24px] font-black italic uppercase tracking-widest text-[#2F353B]">
                Admissão Visual Pericial
              </h3>
              <p className="text-[12px] text-blue-600 font-black italic uppercase tracking-[0.4em] bg-blue-50 py-3 rounded-full inline-block px-10">
                ALOCAÇÃO: {activePhotoSlot?.toUpperCase()}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8">
              <button
                onClick={() => cameraInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-[#2F353B] text-white p-12 rounded-[3.5rem] flex items-center justify-center gap-8 font-black italic text-[18px] shadow-2xl transition-all hover:bg-blue-600 disabled:opacity-50"
              >
                <Zap size={28} /> {loadingCaptura ? 'VALIDANDO...' : 'ATIVAR LENTE DE CAMPO'}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loadingCaptura}
                className="w-full bg-white border-4 border-slate-100 text-slate-400 p-12 rounded-[3.5rem] flex items-center justify-center gap-8 font-black italic text-[16px] hover:border-blue-600 hover:text-blue-600 shadow-sm disabled:opacity-50"
              >
                <ImageIcon size={28} /> IMPORTAR ASSET DIGITAL
              </button>
            </div>
            <button
              onClick={() => { setShowPhotoOptions(false); setActivePhotoSlot(null); }}
              className="w-full text-[13px] text-slate-300 font-black italic tracking-[0.6em] uppercase hover:text-red-600 transition-colors pt-6"
            >
              CANCELAR OPERAÇÃO
            </button>
          </div>
        </div>
      )}
    </div>
  );
}